/**
 * Opaque session API for GdChart. ReScript owns LiveView lifecycle only;
 * ECharts types and option building stay in this TS layer.
 */
import echarts from './echarts.ts';
import type { ECharts } from './echarts-types.ts';
import type { ChartConfig } from './types.ts';
import { buildOption } from './options/index.ts';
import { ensureWorldMapRegistered } from './world-map.ts';
import { attachExportButton } from './export.ts';
import { isDark, resetBrandColorsCache } from './theme.ts';

export type GdChartPushEvent = (
  event: string,
  payload: Record<string, string>
) => void;

export type GdChartSession = {
  sync: () => void;
  destroy: () => void;
};

function parseConfig(raw: string): ChartConfig | null {
  try {
    return JSON.parse(raw) as ChartConfig;
  } catch {
    return null;
  }
}

function chartRenderer(config: ChartConfig): 'svg' | 'canvas' {
  // Canvas hit-testing is steadier for dense sunburst outside labels (SVG
  // flicker on overlapping radial text). Heatmap/world_map need canvas too.
  return config.type === 'heatmap' ||
    config.type === 'world_map' ||
    config.type === 'sunburst'
    ? 'canvas'
    : 'svg';
}

/** Chart PNG/SVG download control — off globally (overlays compact rings / dense Hub chrome). */
function isExportable(_config: ChartConfig): boolean {
  return false;
}

function stackedBarWantsZoomEvents(config: ChartConfig): boolean {
  return config.type === 'stacked_bar' && config.showDataZoom === true;
}

/** Ignore tiny parent layout noise (tooltips / sibling LiveView patches). */
const RESIZE_EPSILON_PX = 8;

/** Resolve visible category labels after a dataZoom interaction. */
export function visibleCategoryLabels(chart: ECharts): {
  start_label: string;
  end_label: string;
} | null {
  const opt = chart.getOption() as {
    xAxis?: Array<{ data?: string[] }> | { data?: string[] };
    dataZoom?: Array<{
      startValue?: string | number;
      endValue?: string | number;
      start?: number;
      end?: number;
    }>;
  };

  const xAxis = Array.isArray(opt.xAxis) ? opt.xAxis[0] : opt.xAxis;
  const labels = xAxis?.data;
  if (!Array.isArray(labels) || labels.length === 0) return null;

  const dzList = opt.dataZoom;
  const dz = Array.isArray(dzList) ? dzList[0] : undefined;
  if (!dz) {
    return {
      start_label: String(labels[0]),
      end_label: String(labels[labels.length - 1]),
    };
  }

  let startIdx = 0;
  let endIdx = labels.length - 1;

  if (dz.startValue !== undefined || dz.endValue !== undefined) {
    if (dz.startValue !== undefined) {
      const i = labels.indexOf(String(dz.startValue));
      if (i >= 0) startIdx = i;
    }
    if (dz.endValue !== undefined) {
      const i = labels.indexOf(String(dz.endValue));
      if (i >= 0) endIdx = i;
    }
  } else if (typeof dz.start === 'number' && typeof dz.end === 'number') {
    startIdx = Math.round((dz.start / 100) * (labels.length - 1));
    endIdx = Math.round((dz.end / 100) * (labels.length - 1));
  }

  if (startIdx > endIdx) {
    const t = startIdx;
    startIdx = endIdx;
    endIdx = t;
  }

  return {
    start_label: String(labels[startIdx]),
    end_label: String(labels[endIdx]),
  };
}

const worldMapUnavailableOption = {
  backgroundColor: 'transparent',
  title: {
    text: 'Map data unavailable',
    left: 'center',
    top: 'middle',
    textStyle: {
      color: 'rgba(148,163,184,0.95)',
      fontSize: 12,
    },
  },
};

/**
 * ECharts must own an exclusive DOM node. Export controls and hook attrs live on
 * the LiveView host; the surface is a dedicated child (Taylor-style split).
 */
function ensureChartSurface(host: HTMLElement): HTMLElement {
  const existing = host.querySelector<HTMLElement>('[data-gd-chart-surface]');
  if (existing) return existing;

  if (getComputedStyle(host).position === 'static') {
    host.style.position = 'relative';
  }

  const surface = document.createElement('div');
  surface.dataset['gdChartSurface'] = '';
  surface.style.width = '100%';
  surface.style.height = '100%';
  host.appendChild(surface);
  return surface;
}

/**
 * Create a chart session bound to a LiveView hook element (`phx-hook="GdChart"`).
 */
export function createGdChartSession(
  el: HTMLElement,
  pushEvent?: GdChartPushEvent
): GdChartSession {
  let chart: ECharts | null = null;
  let lastConfig: string | null = null;
  let resizeCleanup: (() => void) | null = null;
  let worldMapApplyScheduledFor: string | null = null;
  let lazyObserver: IntersectionObserver | null = null;
  let zoomBound = false;
  let zoomTimer: ReturnType<typeof setTimeout> | null = null;
  let lastZoomKey: string | null = null;
  let lastResizeW = 0;
  let lastResizeH = 0;
  let lastIsDark = isDark();
  let themeObserver: MutationObserver | null = null;

  const resizeIfNeeded = (box: DOMRectReadOnly | DOMRect) => {
    if (!chart) return;
    if (box.width < 8 || box.height < 8) return;
    if (
      Math.abs(box.width - lastResizeW) < RESIZE_EPSILON_PX &&
      Math.abs(box.height - lastResizeH) < RESIZE_EPSILON_PX
    ) {
      return;
    }
    lastResizeW = box.width;
    lastResizeH = box.height;
    chart.resize();
  };

  const scheduleDoubleRafResize = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!chart) return;
        resizeIfNeeded(el.getBoundingClientRect());
      });
    });
  };

  const emitZoomRange = () => {
    if (!chart || !pushEvent) return;
    const range = visibleCategoryLabels(chart);
    if (!range) return;
    const key = `${range.start_label}|${range.end_label}`;
    if (key === lastZoomKey) return;
    lastZoomKey = key;
    pushEvent('gd_chart_data_zoom', range);
  };

  const bindZoomEvents = (config: ChartConfig) => {
    if (!chart || !pushEvent || !stackedBarWantsZoomEvents(config)) return;
    if (zoomBound) return;
    zoomBound = true;
    chart.on('dataZoom', () => {
      if (zoomTimer !== null) clearTimeout(zoomTimer);
      zoomTimer = setTimeout(() => {
        zoomTimer = null;
        emitZoomRange();
      }, 100);
    });
  };

  const applyOption = (raw: string, config: ChartConfig) => {
    if (el.dataset['chart'] !== raw) return;
    if (!chart) return;
    chart.setOption(buildOption(config), true);
    lastConfig = raw;
    if (config.type === 'stacked_bar' && config.zoom) {
      lastZoomKey = `${config.zoom.startValue ?? ''}|${config.zoom.endValue ?? ''}`;
    }
    bindZoomEvents(config);
    // Only force a post-paint resize when we have not measured a real box yet.
    if (lastResizeW < 8 || lastResizeH < 8) {
      scheduleDoubleRafResize();
    }
  };

  /** Rebuild colours/inks when light/dark flips without a config change. */
  const reapplyForTheme = () => {
    resetBrandColorsCache();
    const raw = el.dataset['chart'];
    if (!raw || !chart) return;
    const config = parseConfig(raw);
    if (!config) return;
    if (config.type === 'world_map') {
      applyWorldMap(raw, config);
      return;
    }
    chart.setOption(buildOption(config), true);
  };

  if (typeof MutationObserver !== 'undefined') {
    themeObserver = new MutationObserver(() => {
      const nowDark = isDark();
      if (nowDark === lastIsDark) return;
      lastIsDark = nowDark;
      reapplyForTheme();
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });
  }

  const ensureChartShell = (config: ChartConfig, raw: string) => {
    if (chart) return;

    const surface = ensureChartSurface(el);

    // Do NOT pass a theme name — ECharts' built-in 'dark' theme applies an opaque navy
    // background. buildOption sets backgroundColor: 'transparent' instead.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- echarts CJS default
    chart = echarts.init(surface, undefined, {
      renderer: chartRenderer(config),
    });

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const runResize = () => {
      debounceTimer = null;
      if (!chart) return;
      // Size from the LiveView host (fixed h-* classes), not parent siblings.
      resizeIfNeeded(el.getBoundingClientRect());
    };
    const scheduleResize = () => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(runResize, 100);
    };

    const onWinResize = () => {
      scheduleResize();
    };
    window.addEventListener('resize', onWinResize);

    let hostRo: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      hostRo = new ResizeObserver(() => {
        scheduleResize();
      });
      hostRo.observe(el);
    }

    resizeCleanup = () => {
      window.removeEventListener('resize', onWinResize);
      hostRo?.disconnect();
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
    };

    scheduleResize();

    if (isExportable(config) && chart) {
      attachExportButton(el, chart, raw);
    }
  };

  const applyWorldMap = (raw: string, config: ChartConfig) => {
    if (worldMapApplyScheduledFor === raw) return;
    worldMapApplyScheduledFor = raw;
    void ensureWorldMapRegistered().then(
      () => {
        applyOption(raw, config);
      },
      () => {
        if (el.dataset['chart'] !== raw) return;
        worldMapApplyScheduledFor = null;
        chart?.setOption(worldMapUnavailableOption, true);
        lastConfig = raw;
      }
    );
  };

  const sync = () => {
    const raw = el.dataset['chart'];
    if (!raw || raw === lastConfig) return;

    const config = parseConfig(raw);
    if (!config) return;

    if (config.type !== 'world_map') {
      worldMapApplyScheduledFor = null;
    }

    if (config.type === 'world_map') {
      ensureChartShell(config, raw);
      if (config.lazy) {
        lazyObserver?.disconnect();
        lazyObserver = null;
        if (typeof IntersectionObserver === 'undefined') {
          applyWorldMap(raw, config);
          return;
        }
        lazyObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            lazyObserver?.disconnect();
            lazyObserver = null;
            applyWorldMap(raw, config);
          },
          { rootMargin: '120px' }
        );
        lazyObserver.observe(el);
        return;
      }
      applyWorldMap(raw, config);
      return;
    }

    ensureChartShell(config, raw);
    applyOption(raw, config);
  };

  const destroy = () => {
    if (zoomTimer !== null) {
      clearTimeout(zoomTimer);
      zoomTimer = null;
    }
    themeObserver?.disconnect();
    themeObserver = null;
    lazyObserver?.disconnect();
    lazyObserver = null;
    resizeCleanup?.();
    resizeCleanup = null;
    chart?.dispose();
    chart = null;
    worldMapApplyScheduledFor = null;
    lastConfig = null;
    zoomBound = false;
    lastResizeW = 0;
    lastResizeH = 0;
    lastZoomKey = null;
  };

  return { sync, destroy };
}
